import type { JSX } from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import type { RecipeApiItem } from "@recipevault/shared";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/ui/sheet.js";
import { Button } from "@/ui/button.js";
import { extractSharedUrl, hostnameOf } from "./shareText.js";
import { submitUrlImport } from "./api.js";
import { findDuplicateByUrl } from "./duplicates.js";
import { ADD_RECIPE_OPTIONS, addRecipeTarget, type AddRecipeOption } from "./addRecipeOptions.js";
import { DuplicateNote } from "./DuplicateNote.js";
import { ApiError } from "../../shared/http.js";

export interface AddRecipeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const OPTION_ROW_CLASS =
  "flex min-h-12 w-full items-center gap-4 rounded-lg border border-border px-4 py-3 text-left transition-colors hover:border-primary disabled:pointer-events-none disabled:opacity-50";

/**
 * Bottom sheet — the primary "add a recipe" entry point (Plan 12, Task 9).
 * Always offers the four static capture paths (URL / paste text / manual /
 * photo, from `ADD_RECIPE_OPTIONS`); if the clipboard holds a shareable URL
 * on open, prepends a one-tap "Import from clipboard" option. Clipboard
 * access is opportunistic — denied/empty/no-URL all silently fall back to
 * just the four static options (no error UI).
 */
export function AddRecipeSheet({ open, onOpenChange }: AddRecipeSheetProps): JSX.Element {
  const navigate = useNavigate();
  const [clipboardUrl, setClipboardUrl] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<RecipeApiItem | null>(null);
  const [inFlight, setInFlight] = useState(false);

  useEffect(() => {
    if (!open) {
      setClipboardUrl(null);
      setDuplicate(null);
      setInFlight(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const text = await navigator.clipboard.readText();
        const url = extractSharedUrl(text);
        if (!url) return;
        // Resolve the duplicate check before showing the option at all, so it
        // never flashes as a one-tap dispatch and then jumps to a warning.
        const dup = await findDuplicateByUrl(url);
        if (!cancelled) {
          setClipboardUrl(url);
          setDuplicate(dup);
        }
      } catch {
        // Clipboard denied/unsupported (or nothing readable) — silently
        // degrade to the four static options. No error UI (brief requirement).
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  function go(key: AddRecipeOption["key"]): void {
    onOpenChange(false);
    void navigate(addRecipeTarget(key));
  }

  async function dispatchClipboardImport(): Promise<void> {
    if (inFlight || !clipboardUrl) return;
    setInFlight(true);
    try {
      const jobId = await submitUrlImport(clipboardUrl);
      onOpenChange(false);
      await navigate(`/imports/${encodeURIComponent(jobId)}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        toast.error("Monthly cap reached. See Settings → Household.");
      } else {
        toast.error(err instanceof Error ? err.message : "Import failed");
      }
      setInFlight(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="pb-safe">
        <SheetHeader>
          <SheetTitle>Add a recipe</SheetTitle>
        </SheetHeader>
        <ul className="flex flex-col gap-2 px-4 pb-4">
          {clipboardUrl && (
            <li>
              {duplicate ? (
                <div className="space-y-2 rounded-lg border border-border bg-card p-3">
                  <p className="font-medium">
                    Import from clipboard — {hostnameOf(clipboardUrl)}
                  </p>
                  <DuplicateNote title={duplicate.title} />
                  <div className="flex gap-2">
                    <Button
                      asChild
                      variant="outline"
                      className="min-h-12 flex-1"
                      onClick={() => onOpenChange(false)}
                    >
                      <Link to={`/recipes/${duplicate.id}`}>Open</Link>
                    </Button>
                    <Button
                      className="min-h-12 flex-1"
                      disabled={inFlight}
                      onClick={() => void dispatchClipboardImport()}
                    >
                      {inFlight ? "Importing…" : "Import anyway"}
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={inFlight}
                  onClick={() => void dispatchClipboardImport()}
                  className={`${OPTION_ROW_CLASS} border-emerald-600/60 bg-emerald-950/20 hover:border-emerald-500 hover:bg-emerald-900/30`}
                >
                  <span className="text-2xl" aria-hidden>
                    📎
                  </span>
                  <p className="font-medium text-emerald-100">
                    {inFlight
                      ? "Importing…"
                      : `Import from clipboard — ${hostnameOf(clipboardUrl)}`}
                  </p>
                </button>
              )}
            </li>
          )}
          {ADD_RECIPE_OPTIONS.map((opt) => (
            <li key={opt.key}>
              <button type="button" onClick={() => go(opt.key)} className={OPTION_ROW_CLASS}>
                <span className="text-2xl" aria-hidden>
                  {opt.icon}
                </span>
                <div>
                  <p className="font-medium">{opt.title}</p>
                  <p className="text-sm text-muted-foreground">{opt.subtitle}</p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </SheetContent>
    </Sheet>
  );
}
